import _ from 'lodash';
import { BaseItemStore } from '@/trendData/baseItem.store';
import { COMMON_PROPS } from '@/trendData/baseItem.constants';
import { StatisticsSummaryItem } from '@/summaryReport/summaryData.types';
import { AnyProperty } from '@/utilities.types';
import { ITEM_DATA_STATUS } from '@/trendData/trendData.constants';

export class SummaryReportStore extends BaseItemStore<StatisticsSummaryItem> {
  static readonly storeName = 'sqSummaryReportStore';

  initialize() {
    this.state = this.immutable(
      _.defaults(
        {
          items: [],
          isItemsOverlaid: false,
          status: ITEM_DATA_STATUS.INITIALIZING,
        },
        COMMON_PROPS,
      ),
    );
  }

  get items() {
    return this.state.get('items');
  }

  get isItemsOverlaid() {
    return this.state.get('isItemsOverlaid');
  }

  get status() {
    return this.state.get('status');
  }

  dehydrate() {
    return {
      items: _.chain(this.state.get('items'))
        .map((item) => this.pruneDehydratedItemProps(item))
        .value(),
      isItemsOverlaid: this.isItemsOverlaid,
    };
  }

  async rehydrate(dehydratedState: AnyProperty) {
    this.state.merge(dehydratedState);
  }

  setItems(payload: { items: StatisticsSummaryItem[] }) {
    return this.state.set('items', payload.items);
  }

  updateItem(payload: StatisticsSummaryItem) {
    const updatedItems = _.map(this.items, (item) => (item.id === payload.id ? payload : item));
    return this.setItems({ items: updatedItems });
  }

  addItem(payload: StatisticsSummaryItem) {
    const currentItems = this.items;
    if (!this.findItem(payload.id)) {
      return this.setItems({ items: [...currentItems, payload] });
    } else {
      this.updateItem(payload);
    }
  }

  insertItemAtIndex(payload: { item: StatisticsSummaryItem; index: number }) {
    const { item, index } = payload;
    const items = this.items ?? [];
    const without = items.filter((x: any) => x.id !== item.id);
    const clamped = Math.max(0, Math.min(index, without.length));
    const next = [...without.slice(0, clamped), item, ...without.slice(clamped)];
    return this.setItems({ items: next });
  }

  setStatus(payload: { status: ITEM_DATA_STATUS }) {
    this.state.set('status', payload.status);
  }

  setIsItemsOverlaid(payload: { isItemsOverlaid: boolean }) {
    return this.state.set('isItemsOverlaid', payload.isItemsOverlaid);
  }

  removeItems(payload: { items: StatisticsSummaryItem[] }) {
    // Call the parent's removeItems method which handles color tracking
    super.removeItems(payload);
  }

  protected readonly handlers = {
    ...super.baseHandlers,
    SR_SET_ITEMS: this.setItems,
    SR_ADD_ITEM: this.addItem,
    SR_INSERT_ITEM_AT: this.insertItemAtIndex,
    SR_REMOVE_ITEMS: this.removeItems,
    SR_SET_IS_ITEMS_OVERLAID: this.setIsItemsOverlaid,
    SR_SET_STATUS: this.setStatus,
  };
}
